import { featureCollection, point } from '@turf/helpers'
import $ from 'jquery'
import get from 'lodash/get'
import includes from 'lodash/includes'
import isNil from 'lodash/isNil'
import memoize from 'lodash/memoize'
import set from 'lodash/set'
import toLower from 'lodash/toLower'
import toString from 'lodash/toString'
import { toStringXY as olToStringXY } from 'ol/coordinate'
import OLFormatGeoJSON from 'ol/format/GeoJSON'
import OLGeolocation from 'ol/Geolocation'
import OLGeomLineString from 'ol/geom/LineString'
import OLGeomPoint from 'ol/geom/Point'
import OLLayerVector from 'ol/layer/Vector'
import { unByKey as OLObservableUnByKey } from 'ol/Observable'
import OLOverlay from 'ol/Overlay'
import { transform as OLProjTransform } from 'ol/proj'
import OLRenderFeature from 'ol/render/Feature'
import OLSourceVector from 'ol/source/Vector'
import OLStyleCircle from 'ol/style/Circle'
import OLStyleFill from 'ol/style/Fill'
import OLStyleStroke from 'ol/style/Stroke'
import OLStyleStyle from 'ol/style/Style'
import { reduxStore } from 'reduxStore'

import { setDirectionsEndLocation, setDirectionsStartLocation } from 'api/actions'
import { HistoryUpdate } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import PoziDrawerTabInfo from 'drawer/tabs/info/info'
import { ShowWhatsHere, UseWhatsHereInfoPanel, WHATS_HERE_LAYER_TITLEID, WhatsHereClear } from 'drawer/tabs/info/WhatsHereUtils'
import PoziMeasure from 'drawer/tabs/tools/measure'
import geolocateMaker from 'img/geolocation_marker.png'
import selectMaker from 'img/select_marker.svg'
import GPSStart from 'map/GPSStart'
import IsPointInBBox from 'map/IsPointInBBox'
import PoziZoom from 'map/zoom'
import GetActiveTabInfo from 'utils/GetActiveTabInfo'
import { getFeatureInfoAtPixel, getMap, runDirections, runWhatsHere, runWhatsThese, runWhatsThis } from 'utils/MapUtils'
import PoziNotifications from 'utils/notifications'
import { targetSet, targetUnset, CURRENT_TARGET_LAYER_TITLEID, currentTargetLayerClear } from 'utils/target'

import { setMapContextMenu } from './actions'

/**
 * PoziMapAdapter
 */
const PoziMap = memoize(function () {
  // Adapters
  const adapters = {
    init: function () {
      return init()
    },
    gpsInit: function () {
      return gpsInit()
    },
    onChangePosition: function (evt) {
      return onChangePosition(evt)
    },
    rightClickMap: function (evt) {
      return onRightClickMap(evt)
    },
    onSingleClickMap: function (evt) {
      return onSingleClickMap(evt)
    },
    enableDefaultMapClickInteration: function () {
      return enableDefaultMapClickInteration()
    },
    disableDefaultMapClickInteration: function () {
      return disableDefaultMapClickInteration()
    },
    getMarkerPosition: function () {
      return getMarkerPosition()
    },
    hideMarker: function () {
      return hideMarker()
    },
    removeMarker: function () {
      return removeMarker()
    },
    showMarker: function () {
      return showMarker()
    },
    unselectAllFeaturesStylingOnly: function (thisOlLayerOnly) {
      return unselectAllFeaturesStylingOnly(thisOlLayerOnly)
    },
    unselectAllFeatures: function () {
      return unselectAllFeatures()
    },
    showFeaturesInLayer: function (layer, prop, val) {
      return showFeaturesInLayer(layer, prop, val)
    },
    showFeaturesInLayerWithValArray: function (titleid, prop, valArr) {
      return showFeaturesInLayerWithValArray(titleid, prop, valArr)
    },
    addLayerFromGeoJSON: function (geojsonObject) {
      return addLayerFromGeoJSON(geojsonObject)
    },
    fadeOutLayer: function (olLayer, interval) {
      return fadeOutLayer(olLayer, interval)
    },
    setCursor: function (options) {
      return setCursor(options)
    },
    addLayer: function (olLayer) {
      return addLayer(olLayer)
    },
    removeLayer: function (olLayer) {
      return removeLayer(olLayer)
    },
    addInteraction: function (olInteraction) {
      return addInteraction(olInteraction)
    },
    removeInteraction: function (olInteraction) {
      return removeInteraction(olInteraction)
    },
    setSingleClickInteraction: function (func, olInteractionDraw) {
      return setSingleClickInteraction(func, olInteractionDraw)
    },
    setRightClickInteraction: function (func, olInteractionDraw) {
      return setRightClickInteraction(func, olInteractionDraw)
    },
    setDoubleClickInteraction: function (func, olInteractionDraw) {
      return setDoubleClickInteraction(func, olInteractionDraw)
    },
    setSelectMarker: function (olOverlaySelectMarker) {
      return setSelectMarker(olOverlaySelectMarker)
    },
    refreshAllLayers: function () {
      return refreshAllLayers()
    },
    resize: function () {
      return resize()
    },
    setCenter: function (olCoordinate) {
      return setCenter(olCoordinate)
    },
    setMapDefaults: function (evt) {
      return setMapDefaults(evt)
    }
  }

  const app = PoziUtils.getApp()

  var init = function () {}

  // @param olCoordinate An array of numbers representing an xy coordinate. Example: [16, 48].
  // https://openlayers.org/en/latest/apidoc/ol.html#.Coordinate
  var setCenter = function (latlng) {
    if (!isNil(latlng)) {
      const mcenterPoint = new OLGeomPoint(latlng)
      const mcenterPointTransformed = mcenterPoint.transform('EPSG:4326', 'EPSG:3857')
      const olCoordinate = mcenterPointTransformed.getCoordinates()
      const args = {}
      args.center = olCoordinate
      app.map.getView().animate(args) // animate is less jaring than setCenter.
      // app.map.getView().setCenter(olCoordinate) // Center without animation
    }
  }

  const resize = () => {
    if (!app?.map) {
      console.log('resize called before map was initialised. This should not happen.')
      return
    }

    HistoryUpdate()

    const drawerVisible = Model.get('app.event.drawer.visible', false)
    const desktopMode = Model.get('app.event.window.mode', 'desktop') === 'desktop'
    const drawerHeight = $('.pozi-layout-drawer-show').height()
    const drawerWidth = $('.pozi-layout-drawer-show').width()
    const windowHeight = $(window).height()
    const windowWidth = $(window).width()

    // console.log('resize', windowHeight, windowWidth, drawerWidth)

    if (drawerVisible) {
      if (desktopMode) {
        $('#map,#gmap').width(windowWidth - drawerWidth)
        $('#map,#gmap').height(windowHeight)
        const middle = (windowWidth - drawerWidth) / 2 + drawerWidth
        $('#logoDiv').css('left', middle)
        $('#message_box').css('left', drawerWidth)
      } else {
        $('#map,#gmap').height(windowHeight - drawerHeight)
        $('#map,#gmap').width(windowWidth)
        $('#logoDiv').css('left', '10px')
        $('#message_box').css('left', '200px')
        $('#_hj_feedback_container').hide()
      }
    } else {
      $('#map,#gmap').height(windowHeight)
      $('#map,#gmap').width(windowWidth)
      $('#message_box').css('left', '200px')
      if (desktopMode) {
        $('#logoDiv').css('left', '50%')
      } else {
        $('#logoDiv').css('left', '10px')
      }
    }

    app.map.updateSize()
  }

  var refreshAllLayers = function () {
    console.log('Refresh all layers')
    const olLayers = app.map.getAllLayers()
    for (const key in olLayers) {
      const olLayer = olLayers[key]
      const layer = PoziUtils.getLayerFromOLLayer(olLayer)
      if (isNil(layer)) {
        // Invalid id
        continue
      }

      const olSource = olLayer.getSource()
      if (isNil(olSource)) {
        continue
      }

      if (typeof olSource.getParams === 'function') {
        const params = olSource.getParams()
        set(params, 'nocache', Date.now())
        if (typeof olSource.updateParams === 'function') {
          olSource.updateParams(params)
          console.log('Ensure fresh non-cached version of refreshed layer.', layer.titleid)
        }
      }

      if (typeof olSource.clear === 'function') {
        olSource.clear(true)
      }

      if (typeof olSource.refresh === 'function') {
        olSource.refresh()
      }
    }
    return true
  }

  var addLayer = function (olLayer) {
    const olMap = getMap()
    if (isNil(olMap)) {
      console.error('Invalid map object.')
      return false
    }
    if (isNil(olLayer)) {
      console.error('Invalid layer object passed to addLayer.')
      return false
    }
    olMap.addLayer(olLayer)
    return true
  }

  var removeLayer = function (olLayer) {
    const olMap = getMap()
    if (isNil(olMap)) {
      console.error('Invalid map object.')
      return false
    }
    if (isNil(olLayer)) {
      console.log('Invalid layer object passed to removeLayer, already removed?')
      return false
    }
    olMap.removeLayer(olLayer)
    olMap.render() // Refresh layers

    return true
  }

  var setCursor = function (options) {
    if (options.enable) {
      $('#map').removeClass('map-cursor-default')
      $('#map').addClass(options.class)
    } else {
      $('#map').removeClass(options.class)
      $('#map').addClass('map-cursor-default')
    }
  }

  let intervalID
  let intervalIDOpacity

  const setLayerOpacity = function (olLayer) {
    intervalIDOpacity -= 0.1
    olLayer.setOpacity(intervalIDOpacity)
    if (intervalIDOpacity <= 0) {
      clearInterval(intervalID)
      app.map.removeLayer(olLayer)
    }
    app.map.render() // Refresh layers
  }

  var fadeOutLayer = function (olLayer, interval = 100) {
    intervalIDOpacity = 1
    intervalID = window.setInterval(setLayerOpacity, interval, olLayer)
  }

  var addLayerFromGeoJSON = function (geojsonObject) {
    const options = {
      defaultDataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    }
    const features = new OLFormatGeoJSON(options).readFeatures(geojsonObject)
    const vectorLayer = new OLLayerVector({
      source: new OLSourceVector({ features }),
      style: styleFunction
    })
    app.map.addLayer(vectorLayer)
    return vectorLayer
  }

  const getDefaultStyles = function () {
    const image = new OLStyleCircle({
      radius: 5,
      fill: null,
      stroke: new OLStyleStroke({ color: 'red', width: 1 })
    })

    const styles = {
      Point: new OLStyleStyle({
        image
      }),
      LineString: new OLStyleStyle({
        stroke: new OLStyleStroke({
          color: 'green',
          width: 1
        })
      }),
      MultiLineString: new OLStyleStyle({
        stroke: new OLStyleStroke({
          color: 'green',
          width: 1
        })
      }),
      MultiPoint: new OLStyleStyle({
        image
      }),
      MultiPolygon: new OLStyleStyle({
        stroke: new OLStyleStroke({
          color: 'blue',
          lineDash: [4],
          width: 3
        }),
        fill: new OLStyleFill({
          color: 'rgba(255, 255, 0, 0.1)'
        })
      }),
      Polygon: new OLStyleStyle({
        stroke: new OLStyleStroke({
          color: 'blue',
          lineDash: [4],
          width: 3
        }),
        fill: new OLStyleFill({
          color: 'rgba(0, 0, 255, 0.1)'
        })
      }),
      GeometryCollection: new OLStyleStyle({
        stroke: new OLStyleStroke({
          color: 'blue',
          lineDash: [4],
          width: 3
        }),
        fill: new OLStyleFill({
          color: 'magenta'
        }),
        image: new OLStyleCircle({
          radius: 10,
          fill: null,
          stroke: new OLStyleStroke({
            color: 'magenta'
          })
        })
      }),
      Circle: new OLStyleStyle({
        stroke: new OLStyleStroke({
          color: 'red',
          width: 2
        }),
        fill: new OLStyleFill({
          color: 'rgba(255,0,0,0.2)'
        })
      })
    }

    return styles
  }

  var styleFunction = function (feature) {
    const styles = getDefaultStyles()
    return styles[feature.getGeometry().getType()]
  }

  // thisLayerOnly is optional
  var unselectAllFeaturesStylingOnly = function (thisOlLayerOnly) {
    console.debug('Unselect All Features styling only ...', thisOlLayerOnly)

    // List of internal layers to ignore
    const excludedLayers = [Model.ANNOTATION_LAYER_TITLEID, CURRENT_TARGET_LAYER_TITLEID]

    if (!app.map) {
      console.warn('map - unselectAllFeaturesStylingOnly called before map was initialised!')
      return
    }

    const olLayers = app.map.getAllLayers()

    for (const key in olLayers) {
      const olLayer = olLayers[key]
      if (!olLayer) {
        continue
      }

      const layerConf = PoziUtils.getLayerFromOLLayer(olLayer)
      if (!layerConf) {
        continue
      }

      const { title = '', titleid = '' } = layerConf

      // Ignore some internal layers
      if (excludedLayers.includes(titleid)) {
        continue
      }

      if (!isNil(thisOlLayerOnly)) {
        const layerOnly = PoziUtils.getLayerFromOLLayer(thisOlLayerOnly)
        if (titleid !== layerOnly.titleid) {
          continue
        }
      }

      const defaultStyle = get(app, 'layerConfig[' + title + '].style["default"]', null)
      if (isNil(defaultStyle)) {
        // console.log('Unable to find default style for layer', title)
        continue
      }

      const olSource = olLayer.getSource()
      if (!olSource) continue

      if (!olSource.forEachFeature) continue

      // Not sure why.. getting Don't make functions within a loop  no-loop-func
      olSource.forEachFeature((olFeature) => {
        const currentStyleVisible = olFeature.get('visible') ?? true
        const isRenderFeature = olFeature instanceof OLRenderFeature

        // Render features cannot have their styles set
        if (currentStyleVisible && !isRenderFeature) {
          olFeature.setStyle(defaultStyle)
        } else {
          console.log('Ignore invisible features.')
        }
      })
    }
  }

  /**
   * Loop though features set to default style.
   */
  var unselectAllFeatures = function () {
    console.log('Unselect All Features ...')
    Model.unset('app.state.feature')
    Model.set('app.event.features.unselect.all', true)

    WhatsHereClear()

    // Clear the current target layer
    currentTargetLayerClear()
  }

  const showFeatureWithPropValElseHide = function (olFeature, prop, valLower, defaultStyle, selectedStyle, hiddenStyle) {
    // console.debug('showFeatureWithPropValElseHide', olFeature)

    const currentStyle = olFeature.getStyle()
    let style = defaultStyle
    if (currentStyle && currentStyle === selectedStyle) {
      style = selectedStyle
    }

    if (!prop || !valLower) {
      olFeature.setStyle(style)
      olFeature.set('visible', true, false)
      return
    }

    const turfFeature = PoziUtils.convertOLFeatureToTurfFeature(olFeature)
    if (isNil(turfFeature) || isNil(turfFeature.properties)) {
      olFeature.setStyle(hiddenStyle)
      olFeature.set('visible', false, false)
      return
    }

    if (turfFeature.properties[prop]) {
      const propValueLower = toLower(toString(turfFeature.properties[prop]))
      if (valLower === propValueLower) {
        olFeature.setStyle(style)
        olFeature.set('visible', true, false)
      } else {
        olFeature.setStyle(hiddenStyle)
        olFeature.set('visible', false, false)
      }
    } else {
      olFeature.setStyle(hiddenStyle)
      olFeature.set('visible', false, false)
    }
  }

  /**
   * Loop though features set to default style.
   */
  var showFeaturesInLayer = function (layerConf, prop, val) {
    console.debug('showFeaturesInLayer ...', layerConf, prop, val)

    const olLayers = app.map.getAllLayers()
    if (!olLayers) return
    for (const key in olLayers) {
      const olLayer = olLayers[key]
      if (!olLayer) continue
      const layerFromOLLayer = PoziUtils.getLayerFromOLLayer(olLayer)
      // If the map layer does not have an associated layer config, skip it
      // This is currently the case with the layer that is used for the buffer select tool
      if (!layerFromOLLayer) continue
      // If the map layer is not enabled and selected on, skip it
      if (!layerFromOLLayer.selected) continue
      var defaultStyle = get(app, 'layerConfig[' + layerFromOLLayer.title + '].style["default"]', null)
      if (layerFromOLLayer.titleid !== layerConf.titleid) continue
      const olSource = olLayer.getSource()
      if (!olSource) continue
      if (!olSource.forEachFeature) continue
      var valLower = toLower(toString(val))
      var hiddenStyle = new OLStyleStyle()
      var selectedStyle = get(app, 'layerConfig[' + layerFromOLLayer.title + '].style["selected"]', null)
      if (isNil(selectedStyle)) {
        console.log('Unable to find selected style for layer', layerFromOLLayer.title)
        selectedStyle = defaultStyle
      }

      olSource.forEachFeature(function (olFeature) {
        showFeatureWithPropValElseHide(olFeature, prop, valLower, defaultStyle, selectedStyle, hiddenStyle)
      })
    }
  }

  /**
   * Loop though features set to default style.
   */
  var showFeaturesInLayerWithValArray = function (titleid, prop, valArr) {
    console.debug('showFeaturesInLayerWithValArray ...', titleid, prop, valArr)

    const olLayers = app.map.getAllLayers()
    if (!olLayers) return
    for (const key in olLayers) {
      const olLayer = olLayers[key]
      if (!olLayer) continue
      const layerFromOLLayer = PoziUtils.getLayerFromOLLayer(olLayer)
      // If the map layer does not have an associated layer config, skip it
      // This is currently the case with the layer that is used for the buffer select tool
      if (!layerFromOLLayer) continue
      // If the map layer is not enabled and selected on, skip it
      if (!layerFromOLLayer.selected) continue
      var defaultStyle = get(app, 'layerConfig[' + layerFromOLLayer.title + '].style["default"]', null)
      if (layerFromOLLayer.titleid !== titleid) continue
      const olSource = olLayer.getSource()
      if (!olSource) continue
      if (!olSource.forEachFeature) continue
      var hiddenStyle = new OLStyleStyle()
      // Not sure why.. getting Don't make functions within a loop  no-loop-func

      olSource.forEachFeature(function(olFeature) {
        showFeatureWithPropValArrElseHide(olFeature, prop, valArr, defaultStyle, hiddenStyle)
      })
    }
  }

  var showFeatureWithPropValArrElseHide = function (olFeature, prop, valArr, defaultStyle, hiddenStyle) {
    if (!olFeature) {
      return
    }

    if (!prop) {
      const id = olFeature.getId()
      if (!id) {
        return
      }
      prop = id
    }

    if (!valArr || valArr.length === 0) {
      olFeature.setStyle(hiddenStyle)
      olFeature.set('visible', false, false) // key, value, silent
      return
    }

    const turfFeature = PoziUtils.convertOLFeatureToTurfFeature(olFeature)
    if (!turfFeature || !turfFeature.properties) return
    if (turfFeature.properties[prop]) {
      const propValueLower = toLower(toString(turfFeature.properties[prop]))
      const valArrLower = valArr.map(v => v.toString().toLowerCase())
      if (includes(valArrLower, propValueLower)) {
        olFeature.setStyle(defaultStyle)
        olFeature.set('visible', true, false) // key, value, silent
      } else {
        olFeature.setStyle(hiddenStyle)
        olFeature.set('visible', false, false) // key, value, silent
      }
    } else {
      olFeature.setStyle(hiddenStyle)
      olFeature.set('visible', false, false) // key, value, silent
    }
  }

  var onChangePosition = function (evt) {
    const pos = evt.target.get('position')

    console.debug('Map - onChangePosition - select marker change position event triggered', pos, evt.target)

    if (!Array.isArray(pos)) {
      console.error('Map - onChangePosition -  position is not valid.', pos)
      return
    }

    const coords4326 = app.transformTo4326(pos)

    console.debug('Map - onChangePosition - coords', coords4326)

    if (isNaN(coords4326[0])) {
      console.debug('Map - onChangePosition - Marker position not defined.')
    } else {
      console.debug('Map - onChangePosition - Marker position ' + coords4326.join(','), coords4326)

      // Reverse to get lat/lon values instead of lon/lat and
      // round coordinates to 3 digits to prevent too detailed locations
      const coordsLatLon = [...coords4326].reverse()
      const coordsRounded = olToStringXY(coordsLatLon, 3)

      PoziUtils.gtag({
        category: 'useraction',
        status: 'success',
        identifier: coordsRounded,
        action: 'Map clicked'
      })

      setTarget(evt)

      console.debug('Map - onChangePosition - update marker postion', coords4326)

      if (coords4326.length === 2) {
        HistoryUpdate()
        window.store.dispatch(setDirectionsStartLocation())
        window.store.dispatch(setDirectionsEndLocation(coords4326))
      }

      if (UseWhatsHereInfoPanel()) {
        const featureCollectionT = featureCollection([point(coords4326)])
        ShowWhatsHere(featureCollectionT)
      } else {
        app.activateMode('default') // Activate the default mode i.e. all non-vector tabs
      }

      PoziMap().showMarker()
    }
  }

  var setTarget = function (evt) {
    // console.log('PoziMap: Set target to point type...')
    const pointOL = new OLGeomPoint(evt.target.get('position'))
    const sourceProj = app.map.getView().getProjection()
    const geom = pointOL.clone().transform(sourceProj, 'EPSG:4326')
    // var coordinates = geom.getCoordinates()
    const geoJSON = new OLFormatGeoJSON()
    const geomGeoJSONString = geoJSON.writeGeometryObject(geom, {})
    // console.log(geomGeoJSONString)
    // console.log(coordinates)
    const coords = get(geomGeoJSONString, 'coordinates', null)
    if (!coords) {
      console.error('Unable to get coordinates from click target.')
      targetUnset()
      return
    }
    const targetPoint = point(coords, { mapclick: true })
    const featurecollectionT = featureCollection([targetPoint])
    targetSet(featurecollectionT, { mapclick: true })
  }

  var setSingleClickInteraction = function (func, olInteractionDraw) {
    console.debug('setSingleClickInteraction', func, olInteractionDraw)

    let mapSingleClickInteraction = Model.get('app.map.click.single.interaction.on', null)
    if (!isNil(mapSingleClickInteraction)) {
      console.debug('setSingleClickInteraction - remove previous single click interaction', mapSingleClickInteraction)

      OLObservableUnByKey(mapSingleClickInteraction)
    }

    const mapDoubleClickInteraction = Model.get('app.map.click.double.interaction.on', null)
    if (!isNil(mapDoubleClickInteraction)) {
      OLObservableUnByKey(mapDoubleClickInteraction)
      const olInteractionDrawOldDouble = Model.get('app.map.click.double.interaction.draw', null)
      if (!isNil(olInteractionDrawOldDouble)) {
        PoziMap().removeInteraction(olInteractionDrawOldDouble)
        Model.unset('app.map.click.double.interaction.draw')
      }
    }

    if (isNil(func)) {
      func = onSingleClickMap
    }

    const olMap = getMap()
    if (isNil(olMap)) {
      console.error('Invalid map object.')
      return false
    }

    mapSingleClickInteraction = olMap.on('singleclick', func)
    Model.set('app.map.click.single.interaction.on', mapSingleClickInteraction)

    const olInteractionDrawOld = Model.get('app.map.click.single.interaction.draw', null)
    if (!isNil(olInteractionDrawOld)) {
      removeInteraction(olInteractionDrawOld)
      Model.unset('app.map.click.single.interaction.draw')
    }

    if (!isNil(olInteractionDraw)) {
      addInteraction(olInteractionDraw)
      Model.set('app.map.click.single.interaction.draw', olInteractionDraw)
    }

    return true
  }

  const setRightClickInteraction = (func) => {
    console.debug('setRightClickInteraction')

    const olMap = getMap()
    if (isNil(olMap)) {
      console.error('Invalid map object.')
      return false
    }

    const mapTargetEl = olMap.getTargetElement()

    // console.debug('setSingleClickInteraction - mapTarget', mapTargetEl)

    mapTargetEl.oncontextmenu = func
  }

  var setDoubleClickInteraction = function (func, olInteractionDraw) {
    console.debug('setDoubleClickInteraction')

    const mapSingleClickInteraction = Model.get('app.map.click.single.interaction.on', null)
    if (!isNil(mapSingleClickInteraction)) {
      OLObservableUnByKey(mapSingleClickInteraction)
    }

    let mapDoubleClickInteraction = Model.get('app.map.click.double.interaction.on', null)
    if (!isNil(mapDoubleClickInteraction)) {
      OLObservableUnByKey(mapDoubleClickInteraction)
    }

    if (isNil(func)) {
      console.error('Must pass function to set double click interaction')
      return
    }

    const olMap = getMap()
    if (isNil(olMap)) {
      console.error('Invalid map object.')
      return false
    }

    mapDoubleClickInteraction = olMap.on('dblclick', func)
    Model.set('app.map.click.double.interaction.on', mapDoubleClickInteraction)

    const olInteractionDrawOld = Model.get('app.map.click.double.interaction.draw', null)
    if (!isNil(olInteractionDrawOld)) {
      PoziMap().removeInteraction(olInteractionDrawOld)
      Model.unset('app.map.click.double.interaction.draw')
    }

    if (!isNil(olInteractionDraw)) {
      PoziMap().addInteraction(olInteractionDraw)
      Model.set('app.map.click.double.interaction.draw', olInteractionDraw)
    }
    return true
  }

  var enableDefaultMapClickInteration = function () {
    console.debug('enableDefaultMapClickInteration')

    setSingleClickInteraction(onSingleClickMap)

    setRightClickInteraction(onRightClickMap)
  }

  var disableDefaultMapClickInteration = function () {
    const mapSingleClickInteraction = Model.get('app.map.click.single.interaction.on', null)
    if (!isNil(mapSingleClickInteraction)) {
      OLObservableUnByKey(mapSingleClickInteraction)
    }
  }

  var setSelectMarker = function (olOverlaySelectMarker) {
    const olMap = getMap()
    if (isNil(olMap)) {
      console.warn('setSelectMarker - map not initialised yet.')
      return false
    }

    const olOverlayOld = Model.get('app.marker.olOverlay', null)
    if (!isNil(olOverlayOld)) {
      olMap.removeOverlay(olOverlayOld)
    }

    if (isNil(olOverlaySelectMarker)) {
      const selectEl = document.createElement('img')
      selectEl.crossOrigin = 'anonymous'
      selectEl.src = selectMaker
      selectEl.width = 50
      selectEl.height = 50

      olOverlaySelectMarker = new OLOverlay({
        positioning: 'center-center',
        element: selectEl,
        stopEvent: false
      })
    }

    app.selectMarker = olOverlaySelectMarker
    olMap.addOverlay(olOverlaySelectMarker)
    Model.set('app.marker.olOverlay', app.selectMarker)
  }

  var addInteraction = function (olInteraction) {
    const olMap = getMap()

    if (isNil(olMap)) {
      console.error('Invalid map object.')
      return false
    }
    if (isNil(olInteraction)) {
      console.error('Invalid interaction object.')
      return false
    }
    olMap.addInteraction(olInteraction)
    return true
  }

  var removeInteraction = function (olInteraction) {
    const olMap = getMap()
    if (isNil(olMap)) {
      console.error('Invalid map object.')
      return false
    }
    if (isNil(olInteraction)) {
      console.error('Invalid interaction object.')
      return false
    }
    olMap.removeInteraction(olInteraction)
    return true
  }

  const getCoordsFromMapClickEvent = function (evt) {
    try {
      const coords4326 = app.transformTo4326(evt.coordinate)
      if (isNaN(coords4326[0]) || isNaN(coords4326[1])) {
        console.error('!!! PoziMap: Click coords invalid !!.')
        return null
      }
      console.log('PoziMap: Click details - coords: ' + coords4326 + ' reversed: ' + coords4326[1] + ' ' + coords4326[0] + ', pixel: ' + evt.pixel)
      return coords4326
    } catch (e) {
      console.log(e)
      return null
    }
  }

  // TODO: This needs to be moved to redux/react style.
  var setMapDefaults = function (evt) {
    // console.debug('Map - setMapDefaults', evt)

    PoziDrawerTabInfo().hide()

    Model.unset('app.map.target.selection.layer')

    Model.set('app.event.map.click.single', true)

    // Model.debugconsole()

    if (!isNil(evt)) {
      if (evt.originalEvent.ctrlKey) {
        console.debug('Map - setMapDefaults - ctrl key pressed.')
        Model.set('app.map.click.controlkey', true)
      } else {
        Model.set('app.map.click.controlkey', false)
        app.poziMap.removeAllOverlayFeatures()
      }

      Model.set('app.event.map.click.right', false) // undo any right-click state in model
      Model.unset('app.map.click.titleid') // reset layer that was right-clicked on
    }

    // PoziMeasure().clear()

    const bControlkey = Model.get('app.map.click.controlkey', false)
    if (!bControlkey) {
      PoziMap().unselectAllFeatures()
    }

    // Removing feature overlay
    // app.featureOverlayUnhighlight();
    // Cancel the target
    Model.unset('app.state.feature') // Clear any old features.

    HistoryUpdate()
  }

  const onSingleClickMap = (evt) => {
    console.debug('map - onSingleClickMap', { ...evt })

    const currentTarget = reduxStore.getState().currentTarget

    // Do some processing based on the event
    setMapDefaults(evt)

    const coords4326 = getCoordsFromMapClickEvent(evt)
    if (isNil(coords4326)) {
      return
    }

    PoziDrawerTabInfo().clearState()

    const activeTabInfo = GetActiveTabInfo()
    const activeTabId = get(activeTabInfo, 'activeTabId', '')
    const searchDirectionsExpanded = get(activeTabInfo, 'searchDirectionsExpanded', false)

    // Directions
    if (activeTabId === 'search' && searchDirectionsExpanded) {
      runDirections(coords4326)
      return
    }

    PoziMeasure().clear() // Hide any select drawings

    const featureInfos = getFeatureInfoAtPixel(evt.pixel)

    // console.debug('map - onSingleClickMap - featureInfos', featureInfos)

    // When using CTL-click, if there already were selected features, add the new features.
    // If no new features are present, keep the existing selection.
    const ctrlClick = evt.originalEvent.ctrlKey

    // We want to use the original target for selecting features in case the user has
    // descended into the child targets.
    const originalTarget = currentTarget?.originalTarget || currentTarget
    const originalTargetIsWhatsHere = originalTarget?.titleid === WHATS_HERE_LAYER_TITLEID

    if (ctrlClick && !originalTargetIsWhatsHere) {
      console.debug('map - onSingleClickMap - ctrlClick and currentTarget: multiple features (to be) selected', originalTarget)

      runWhatsThese(featureInfos, originalTarget)

      return
    }

    // When using ALT-click of if there were no features under the click
    // location, then run a normal what's here where the user has clicked
    const altClick = evt.originalEvent.altKey
    if ((altClick || featureInfos.length === 0) && !ctrlClick) {
      runWhatsHere(evt.coordinate)

      return
    }

    // Only return the top/first queryable feature found at the pixel/click position.
    // In the future we are likely to need the other underlying features as well.
    const firstFeatureInfo = featureInfos[0]
    const firstFeature = firstFeatureInfo?.olFeature
    const firstFeatureGeom = firstFeature?.getGeometry()
    const geomType = firstFeatureGeom?.getType()
    const geomTypeSimple = PoziUtils.getGeomTypeSimple(geomType)

    const hasGeom = geomTypeSimple !== 'nogeometry'

    // If the first feature doesn't have a geometry, just run a normal what's here.
    // Note: this should never happen.
    if (!hasGeom) {
      console.log('map - onSingleClickMap - first found feature has no geom. Falling back to normal whatshere....')

      runWhatsHere(evt.coordinate)

      return
    }

    const firstFeatureIsPoint = geomTypeSimple === 'point'
    const firstFeatureIsPolygon = geomTypeSimple === 'polygon'

    // If there is only one feature and if it's not a polygon, run what's this on that feature
    if (featureInfos.length === 1 && !firstFeatureIsPolygon) {
      runWhatsThis(firstFeatureInfo)

      return
    }

    // Store the titleid of the layer of the first feature for use in map/actions.js
    // This will allow for the layer that the clicked feature belongs to to be shown first
    //
    // TODO: move this outside of this method as it introduces side effects
    const titleid = firstFeatureInfo.olLayer ? firstFeatureInfo.olLayer.get('titleid') : null
    Model.set('app.map.click.titleid', titleid)

    // If we click on anything other than a point feature, run what's here that also shows any intersect layers
    if (!firstFeatureIsPoint) {
      runWhatsHere(evt.coordinate, true)

      return
    }

    // Finally, the first feature is a point and we use its coordinate do a special what's here with any
    // intersect layers.
    const firstFeatureCoordinate = firstFeatureGeom?.getFirstCoordinate() // this flattens multipoint coordinates
    if (!firstFeatureCoordinate) {
      console.log('map - onSingleClickMap - first feature has no coordinate. Not running Whats Here.', titleid, firstFeature)

      return
    }

    runWhatsHere(firstFeatureCoordinate, true)
  }

  const onRightClickMap = (evt) => {
    console.debug('map - onRightClickMap', evt.coordinate, evt.clientX, evt.clientY, evt.originalEvent, { ...evt })

    evt.preventDefault()

    // Do not do anything if drawing/measuring tools are active
    const drawingToolsActive = !!window.store.getState().annotationInfo?.titleid
    const measuringToolsActive = Model.get('app.measure.active', false)
    if (drawingToolsActive || measuringToolsActive) {
      console.debug('map - onRightClickMap - drawing/measuring tools active. Not doing anything.', drawingToolsActive, measuringToolsActive)

      return
    }

    const map = getMap()

    const pixel = map.getEventPixel(evt)
    const coordinate = map.getCoordinateFromPixel(pixel)

    console.debug('map - onRightClickMap - pixel/coordinate', pixel, coordinate)

    const contextMenu = {
      coordinate,
      pixel,
      position: {
        left: evt.clientX,
        top: evt.clientY
      }
    }

    window.store.dispatch(setMapContextMenu(contextMenu))
  }

  var gpsInit = function () {
    if (window.location.protocol === 'https:' || Model.get('app.localhost')) {
      $('#gpsButton').show()
    }

    // Add the geolocation marker to the map as an overlay
    const geolocateEl = document.createElement('img')
    geolocateEl.crossOrigin = 'anonymous'
    geolocateEl.src = geolocateMaker
    geolocateEl.width = 22
    geolocateEl.height = 22

    app.geolocateMarker = new OLOverlay({
      positioning: 'center-center',
      element: geolocateEl,
      stopEvent: false
    })

    app.map.addOverlay(app.geolocateMarker)

    app.geolocation = new OLGeolocation({
      projection: app.map.getView().getProjection(),
      tracking: false,
      trackingOptions: {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0
      }
    })

    app.geolocation.on('error', gpsUnableToFindPositionEvent)
    app.geolocation.on('change:position', gpsChangePositionEvent)

    console.log('Activate the geolocation control on GPS button click')
    $('#gpsButton').on('click', function (evt) {
      GPSStart()
    })

    console.log('Activate the geolocation follow control on GPS Follow button click')
    $('#gpsFollowButton').on('click', function () {
      app.geolocation.setTracking(true)
      Model.set('app.gps.follow', true)
      $('#gpsButtonImage').attr('src', './img/the-noun-project/white/gif-100x100/noun_800054_100x100.gif')
    })

    const autolocate = Model.get('config.autolocate', false)
    if (autolocate) {
      GPSStart()
    }
  }

  /**
   * Issue finding GPS
   */
  var gpsUnableToFindPositionEvent = function (evt) {
    const err = 'GPS position not acquired successfully'
    PoziUtils.gtag({
      category: 'useraction',
      status: 'warning',
      identifier: 'GPS',
      action: 'GPS Button clicked',
      additionalInfo: err
    })

    console.error(err, evt)
    // TODO: paint it red for a couple of seconds?
    $('#gpsButton')
      .animate({ opacity: 0 }, 500)
      .animate({ opacity: 1 }, 500)
      .animate({ opacity: 0 }, 500)
      .animate({ opacity: 1 }, 500)

    // Stop tracking
    evt.target.setTracking(false)
  }

  /**
   * GPS Tracking
   */
  var gpsChangePositionEvent = function (evt) {
    console.log('PoziMap: GPS position acquired successfully')

    console.log('Work out the extent of the region to zoom to based on the precision')
    const loc = evt.target.getPosition()
    const acc = evt.target.getAccuracy() + 25
    const diagonal = new OLGeomLineString([[loc[0] - acc, loc[1] - acc], [loc[0] + acc, loc[1] + acc]])

    const bbox = Model.get('config.layout.map.views.bbox', [])
    const point = OLProjTransform(loc, 'EPSG:3857', 'EPSG:4326')
    const bPointInBBox = IsPointInBBox(point, bbox)
    if (!bPointInBBox) {
      console.log('Point not in bounding box.')
      PoziNotifications().warning('GPS position is outside of site boundary.')
      app.geolocation.setTracking(false)
      return
    }

    console.log('Updating the GPS marker position to', loc)
    app.geolocateMarker.setPosition(loc)
    $(app.geolocateMarker.getElement()).removeClass('hidden')
    $(app.geolocateMarker.getElement())
      .css('opacity', 1)
      .animate({ opacity: 0 }, 500)
      .animate({ opacity: 1 }, 500)

    const bFollowMode = Model.get('app.gps.follow', false)
    console.log('New location returned. Follow mode currently ', bFollowMode)
    evt.target.setTracking(bFollowMode)
    if (bFollowMode) {
      $('#gpsButtonImage').attr('src', './img/the-noun-project/white/gif-100x100/noun_800054_animated.gif')
    } else {
      PoziUtils.gtag({
        category: 'useraction',
        status: 'success',
        identifier: 'GPS',
        action: 'Map Button Click'
      })

      // Removing the click target to allow new card to deploy
      HistoryUpdate()

      $('#gpsButtonImage').attr('src', './img/the-noun-project/white/noun_800054.svg')
      console.log('Zoom/pan to the GPS position')
      PoziZoom().zoomPanTo(diagonal, false)

      console.log('Move green dot there, and reveal it')
      app.selectMarker.setPosition(loc) // Triggers PoziMap().onChangePosition
      PoziMap().showMarker()
    }
  }

  /**
   * Get marker position
   * @returns {point}
   */
  var getMarkerPosition = function () {
    const olMarkerPosition = app.selectMarker.getPosition()
    if (!olMarkerPosition) {
      PoziNotifications().warning('Please select a position on the map first.')
      return null
    }
    const point4326 = OLProjTransform(app.selectMarker.getPosition(), 'EPSG:3857', 'EPSG:4326')
    return point(point4326)
  }

  /**
   * Hide the map marker
   */
  var hideMarker = function () {
    const selectMarkerEl = app.selectMarker.getElement()
    if (selectMarkerEl && !selectMarkerEl.classList.contains('hidden')) {
      // console.debug('hideMarker')

      selectMarkerEl.classList.add('hidden')
    }
  }

  /**
   * Remove the map marker
   */
  var removeMarker = function () {
    hideMarker()

    HistoryUpdate()
  }

  /**
   * Show the map marker
   */
  var showMarker = function () {
    const selectorVisible = Model.get('config.layout.map.selector.visible', true) // Config option to never show selector.
    if (selectorVisible) {
      // console.debug('showMarker')

      const selectMarkerEl = app.selectMarker.getElement()
      if (selectMarkerEl) {
        selectMarkerEl.classList.remove('hidden')
      }
    }
  }

  // Return adapters (must be at end of adapter)
  return adapters
})

export default PoziMap
